import axios from 'axios';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { api_urls } from '../api/api-urls';
import { app_config } from '../api/app-config';
import { getBrandDetails } from '../services/brand-service';
import { persistor, RootState } from '../store';
import { revertAll } from '../store/actions';
import { setBrandDetails } from '../store/reducers/brandSlice';
import { openToast } from '../store/reducers/toastSlice';
import { updateLoginStatus } from '../store/reducers/userSlice';
import { clearStoragePreservingMigrationCookies } from '../utils/migration-cookies';
import { NavigationConfig } from '../utils/navigation-constants';

/**
 * Hook to synchronize logout across multiple browser tabs
 * When localStorage is cleared in one tab (logout), other tabs detect this event
 * and redirect to the login page with an appropriate message
 */
export const useLogoutSync = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const refreshToken = useSelector((state: RootState) => state.user.refreshToken);
    const logoutTriggeredRef = useRef(false); // Flag to prevent multiple logout calls

    useEffect(() => {
        /**
         * Listen for storage events which are triggered when localStorage changes in other tabs
         * This enables cross-tab communication
         */
        const handleStorageChange = async (event: StorageEvent) => {
            // Prevent multiple logout calls
            if (logoutTriggeredRef.current) {
                return;
            }

            // Check if localStorage was cleared or if the auth token was removed
            if (
                event.key === null || // localStorage.clear() triggers event with key=null
                (event.key === 'persist:root' && !event.newValue) // Check if main store was cleared
            ) {
                // Logout was triggered from another tab
                logoutTriggeredRef.current = true;

                try {
                    // Call logout API
                    if (refreshToken) {
                        axios
                            .post(
                                api_urls.Logout,
                                { refreshToken },
                                {
                                    baseURL: app_config.apiDomain,
                                    headers: { 'Content-Type': 'application/json' },
                                    withCredentials: true,
                                },
                            )
                            .catch(err => {
                                console.error('Error calling logout API:', err);
                            });
                    }

                    // Clear Redux store
                    dispatch(revertAll());
                    dispatch(updateLoginStatus(false));

                    // Clear persisted state
                    persistor.purge();

                    // Clear localStorage (preserve migration cookies so user lands on login page)
                    clearStoragePreservingMigrationCookies();

                    // Show logout notification
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: 'You Have Been Logged Out From Another Tab. Please Login Again.',
                            severity: 'error',
                        }),
                    );

                    // Fetch brand details after logout
                    try {
                        const response = await getBrandDetails(false);
                        if (response?.statusCode === 200 && response?.data) {
                            dispatch(setBrandDetails(response.data));
                        }
                    } catch (error) {
                        console.error('Error fetching brand details after logout:', error);
                    }

                    // Redirect to login so this tab shows login page, not update_portal
                    navigate(`${NavigationConfig.Login}?source=login`, { replace: true });
                } catch (error) {
                    console.error('Error handling cross-tab logout:', error);
                    // Force redirect to login if something goes wrong
                    navigate(`${NavigationConfig.Login}?source=login`, { replace: true });
                }
            }
        };

        // Add event listener for storage changes
        window.addEventListener('storage', handleStorageChange);

        // Cleanup
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [dispatch, navigate, refreshToken]);
};
